// Patient selection from the BLE remote (UP/DOWN). The clinician cycles through the detected people
// (MoveNet multi-pose, before the assessment switches to single-pose) and picks who the patient is.
// The selection is a MoveNet tracking id; the assessment still decides whether that person may start.

const MIN_POSE_SCORE = 0.25;
const MIN_KEYPOINT_SCORE = 0.25;
// Selected person not detected for this many frames (~1 s) -> drop the selection
const MAX_MISSING_FRAMES = 30;

// A lost id is picked up again by the nearest person within this distance (video pixels) of where
// they were last seen: the tracker gives a new id to someone it misses for a moment
const MAX_REACQUIRE_DISTANCE = 150;
// Highlight box: how far it moves toward this frame's box (0-1), and how many frames it stays up
// while the patient isn't detected. Keypoints are noisy frame to frame, which made the box jump.
const BOX_SMOOTHING = 0.25;
const BOX_HOLD_FRAMES = 15;

// Centre of a pose in video coordinates, from its confident keypoints
function center(pose) {
  const points = pose.keypoints.filter((kp) => kp && kp.score >= MIN_KEYPOINT_SCORE);
  if (points.length === 0) return null;
  return {
    x: points.reduce((sum, kp) => sum + kp.x, 0) / points.length,
    y: points.reduce((sum, kp) => sum + kp.y, 0) / points.length,
  };
}

function center_x(pose) {
  const c = center(pose);
  return c ? c.x : null;
}

/**
 * The same person in this frame: the pose with `id`, or else (the tracker gave them a new id) the
 * pose nearest to where they were last seen, if close enough. Only a newly tracked pose can be them:
 * someone tracked in the previous frame is someone else (e.g. the therapist standing next to them).
 * @param {Array} poses - MoveNet multi-pose results for the current frame.
 * @param {number} id - Their tracking id.
 * @param {object|null} last_pose - Their pose when last seen.
 * @param {Set|null} known_ids - Tracking ids in the previous frame.
 * @returns {object|null}
 */
export function find_person(poses, id, last_pose, known_ids = null) {
  const people = (poses || []).filter((pose) => pose && pose.id != null);
  const same_id = people.find((pose) => pose.id === id);
  if (same_id || !last_pose) return same_id || null;

  const last = center(last_pose);
  if (!last) return null;
  let nearest = null;
  let min_distance = MAX_REACQUIRE_DISTANCE;
  for (const pose of people) {
    if (known_ids && known_ids.has(pose.id)) continue;
    const c = center(pose);
    if (!c) continue;
    const distance = Math.hypot(c.x - last.x, c.y - last.y);
    if (distance < min_distance) {
      min_distance = distance;
      nearest = pose;
    }
  }
  return nearest;
}

/**
 * Smooths the patient highlight box over frames and keeps it up through short detection gaps.
 * Snaps to the new box when the patient changes.
 */
export class HighlightBox {
  constructor() {
    this.reset();
  }

  reset() {
    this.rect = null;
    this.id = null;
    this.missing_frames = 0;
  }

  /**
   * @param {object|null} rect - This frame's box {x, y, width, height}, or null if the patient isn't detected.
   * @param {number} id - The patient's tracking id.
   * @returns {object|null} The box to draw, or null.
   */
  update(rect, id) {
    if (!rect) {
      this.missing_frames++;
      if (this.missing_frames > BOX_HOLD_FRAMES) this.reset();
      return this.rect;
    }
    this.missing_frames = 0;
    if (!this.rect || id !== this.id) {
      this.rect = { ...rect };
      this.id = id;
      return this.rect;
    }
    for (const key of ["x", "y", "width", "height"]) {
      this.rect[key] += BOX_SMOOTHING * (rect[key] - this.rect[key]);
    }
    return this.rect;
  }
}

export class PatientSelector {
  constructor() {
    this.clear();
  }

  /**
   * People who can be selected, ordered left to right as seen on screen. The canvas is mirrored
   * (camera.flip_horizontal), so on-screen left to right is descending video x.
   * @param {Array} poses - MoveNet multi-pose results for the current frame.
   * @returns {Array} The selectable poses, in on-screen order.
   */
  static candidates(poses) {
    return (poses || [])
      .filter((pose) => pose && pose.id != null && pose.score > MIN_POSE_SCORE)
      .map((pose) => ({ pose, x: center_x(pose) }))
      .filter((entry) => entry.x !== null)
      .sort((a, b) => b.x - a.x)
      .map((entry) => entry.pose);
  }

  has_selection() {
    return this.selected_id !== null;
  }

  clear() {
    this.selected_id = null;
    this.last_pose = null;
    this.missing_frames = 0;
  }

  /**
   * Moves the selection to the next person on screen.
   * @param {Array} poses - MoveNet multi-pose results for the current frame.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @param {number} current_id - Tracking id of the person selected now, or -1/null if nobody is.
   * @returns {object|null} The newly selected pose, or null if nobody is detected.
   */
  cycle(poses, direction, current_id) {
    const people = PatientSelector.candidates(poses);
    if (people.length === 0) return null;

    const index = people.findIndex((pose) => pose.id === current_id);
    let next;
    if (index === -1) {
      // Nobody highlighted yet: start from the edge we're moving away from
      next = direction > 0 ? 0 : people.length - 1;
    } else {
      next = (index + direction + people.length) % people.length;
    }

    this.selected_id = people[next].id;
    this.last_pose = people[next];
    this.missing_frames = 0;
    return people[next];
  }

  /**
   * The selected person in this frame. Call once per frame: it also drops a selection whose person
   * has not been detected for MAX_MISSING_FRAMES (the tracker gives them a new id when they return).
   * @param {Array} poses - MoveNet multi-pose results for the current frame.
   * @param {Set|null} known_ids - Tracking ids in the previous frame (see find_person).
   * @returns {object|null} The selected pose, or null if there is no selection or they are not detected.
   */
  selected_pose(poses, known_ids = null) {
    if (this.selected_id === null) return null;

    // No pose-score filter: a partly visible person's score dips below MIN_POSE_SCORE from frame to
    // frame (e.g. at the edge of Gait's crop), which made the highlight blink
    const pose = find_person(poses, this.selected_id, this.last_pose, known_ids);
    if (pose) {
      this.selected_id = pose.id; // follows a new tracking id
      this.last_pose = pose;
      this.missing_frames = 0;
      return pose;
    }

    this.missing_frames++;
    if (this.missing_frames >= MAX_MISSING_FRAMES) {
      this.clear();
    }
    return null;
  }
}

/**
 * The participant chosen by the therapist with the remote (UP/DOWN), for an assessment's pre-start
 * phase. Nobody is chosen by default. The choice is kept through short detection gaps and new
 * tracking ids, and across the phases of a multi-phase assessment (the person is found again by
 * position after a phase); it's dropped once they haven't been detected for ~1 s.
 */
export class PatientSelection {
  constructor() {
    this.selector = new PatientSelector();
    this.reset();
  }

  // Forgets the selection (e.g. when the assessment restarts)
  reset() {
    this.selector.clear();
    this.patient = null;
    this.latest_poses = null;
    this.known_ids = null; // tracking ids in the previous frame
  }

  has_selection() {
    return this.selector.has_selection();
  }

  /**
   * The selected participant this frame, or null if nobody is selected or they aren't detected.
   * The assessment hands this person over at the start instead of its own pick.
   */
  selected_patient() {
    return this.has_selection() ? this.patient : null;
  }

  /**
   * The poses the start-box checks should look at: with a selection only the selected participant
   * counts, otherwise anyone does (a hand raise or voice start without a selection, as before).
   * @param {Array} poses - MoveNet multi-pose results for the current frame.
   * @returns {Array}
   */
  start_check_poses(poses) {
    if (!this.has_selection()) return poses;
    return this.patient ? [this.patient] : [];
  }

  /**
   * Call every frame while selection is open.
   * @param {Array} poses - MoveNet multi-pose results for the current frame.
   * @returns {object|null} The selected participant this frame, or null.
   */
  update(poses) {
    this.latest_poses = poses;
    this.patient = this.selector.selected_pose(poses, this.known_ids);
    this.known_ids = new Set((poses || []).filter(Boolean).map((pose) => pose.id));
    return this.patient;
  }

  /**
   * Moves the selection to the next person on screen (remote UP/DOWN). With nobody selected, UP
   * selects the leftmost person on screen and DOWN the rightmost.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @returns {object|null} The newly selected pose, or null if nobody is detected.
   */
  cycle(direction) {
    const selected = this.selector.cycle(
      this.latest_poses,
      direction,
      this.patient ? this.patient.id : null
    );
    if (selected) this.patient = selected;
    return selected;
  }
}
